/**
 * The domain model, mirroring api/app/domain/.
 *
 * One definition per concept. The predecessor declared `Waypoint` five times
 * in five files, three of them using x/y for latitude and longitude and two
 * using lat/lon, with hand-written conversions at each boundary. Every one of
 * those conversions was a place to flip a sign.
 */

export type DroneId = string;

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'stale';

export type CommandCode =
  | 'accepted'
  | 'denied'
  | 'unsupported'
  | 'failed'
  | 'timeout'
  | 'not_connected'
  | 'rejected_precondition'
  // A PARAM_SET the aircraft kept at a different value than requested (clamped
  // or rejected). Never retried by the writer -- see api/app/vehicles/mavlink/
  // parameters.py. Surfaces per-parameter in a failsafe apply result.
  | 'value_mismatch';

export interface CommandResult {
  ok: boolean;
  code: CommandCode;
  message: string;
}

/** MAV_CMD values this app plans with. */
export const MavCmd = {
  NavWaypoint: 16,
  NavLoiterUnlim: 17,
  NavLoiterTurns: 18,
  NavLoiterTime: 19,
  NavReturnToLaunch: 20,
  NavLand: 21,
  NavTakeoff: 22,
  DoChangeSpeed: 178,
  DoSetRoi: 201,
} as const;
export type MavCmdValue = (typeof MavCmd)[keyof typeof MavCmd];

export const MavFrame = {
  Global: 0,
  GlobalRelativeAlt: 3,
  GlobalTerrainAlt: 10,
} as const;
export type MavFrameValue = (typeof MavFrame)[keyof typeof MavFrame];

/** Degrees and metres. Never degE7 -- that conversion belongs at the wire edge. */
export interface Waypoint {
  lat: number;
  lon: number;
  alt: number;
  command: MavCmdValue;
  frame: MavFrameValue;
  param1: number;
  param2: number;
  param3: number;
  param4: number;
  autocontinue: boolean;
}

export interface Mission {
  waypoints: Waypoint[];
  onVehicle: boolean;
  groundTrackM: number;
}

/**
 * Named vehicle state, lifted out of telemetry messages by the server.
 *
 * Every field is optional because a vehicle reports what it reports. A missing
 * key means "not observed", never "zero" -- an altitude of 0 and an unknown
 * altitude are different things to an operator standing under the aircraft.
 */
export interface DroneScalars {
  lat?: number;
  lon?: number;
  altRelative?: number;
  altAmsl?: number;
  heading?: number;
  groundSpeed?: number;
  airSpeed?: number;
  verticalSpeed?: number;
  roll?: number;
  pitch?: number;
  yaw?: number;
  throttle?: number;
  armed?: boolean;
  customMode?: number;
  mavType?: number;
  systemStatus?: number;
  batteryPercent?: number;
  batteryVoltage?: number;
  batteryCurrent?: number;
  gpsFixType?: number;
  gpsSatellites?: number;
  gpsHdop?: number;
  currentWaypoint?: number;
}

/** Raw decoded messages, keyed by MAVLink message type. An accumulator. */
export type TelemetryCache = Record<string, Record<string, unknown>>;

export interface Drone {
  droneId: DroneId;
  callsign: string;
  autopilotSysId: number | null;
  state: ConnectionState;
  scalars: DroneScalars;
  telemetry: TelemetryCache;
  link: string;
  simulated: boolean;
  lastMessageAt: number | null;
  /** MAVLink 2 signing actually engaged on this link — not merely configured. */
  signing: boolean;
  /**
   * How much of this aircraft the server sends. 'map' is another team's real
   * aircraft: position, heading, identity and armed state, and nothing else.
   * Details, cameras, logs and commands stay with the team that owns it.
   * Absent means full: an older server only ever sent the caller's own.
   */
  access?: DroneAccess;
}

export type DroneAccess = 'full' | 'map';

/**
 * A sparse observation pushed over the WebSocket.
 *
 * `scalars` overwrite per key. `telemetry` merges per message type into the
 * live cache -- it is never a replacement for the whole cache. Applying it any
 * other way reintroduces the bug that discarded fourteen of every fifteen
 * readings on a real aircraft.
 */
export interface DroneDelta {
  droneId: DroneId;
  scalars: DroneScalars;
  telemetry: TelemetryCache;
  observedAt: number;
}

export type Role = 'viewer' | 'operator' | 'tactical' | 'group_commander' | 'admin';

/**
 * Mirrors api/app/security/models.py Role. Only the order matters here; the
 * server compares its own integers and never reads these.
 */
export const ROLE_RANK: Record<Role, number> = {
  viewer: 0,
  operator: 1,
  tactical: 2,
  group_commander: 3,
  admin: 4,
};

export interface User {
  userId: string;
  username: string;
  role: Role;
  teamIds: string[];
  /** Sees the whole operation and can change none of it. Absent means no. */
  observer?: boolean;
}

/** An inclusive range of ports a team owns on one transport. */
export interface PortRange {
  start: number;
  end: number;
}

export interface Team {
  teamId: string;
  callsign: string;
  displayName: string;
  color: string;
  droneCount: number;
  memberCount?: number;
  /** Null when the team owns no block on that transport. Absent from an older server. */
  tcpPorts?: PortRange | null;
  udpPorts?: PortRange | null;
}

export interface MissionPlan {
  planId: string;
  name: string;
  ownerId: string;
  ownerUsername: string;
  collaboratorIds: string[];
  missions: Record<DroneId, Waypoint[]>;
  updatedAt: number;
}

/**
 * Pre-flight failsafe doctrine: a named set of autopilot FS_* parameters that
 * decides how an aircraft behaves on link loss, GPS loss, and low battery.
 * Mirrors api/app/safety/failsafe_doctrine.py.
 */
export interface FailsafeDoctrine {
  key: string;
  label: string;
  description: string;
  /** True disables the autopilot's return-to-operator failsafes; needs confirm. */
  disablesSafety: boolean;
  /** Airframe-specific truths this doctrine's own name overstates, e.g. a
   * "hold" doctrine that actually continues the mission on this vehicle
   * class because the firmware has no loiter-on-failsafe action for it. */
  caveats: string[];
}

export interface FailsafeInfo {
  droneId: DroneId;
  /**
   * The doctrine read back off the aircraft's FS_* parameters, or null if it
   * could not be read (no link) or matches none of the known tables. See
   * `currentDetail` for which.
   */
  current: string | null;
  /** "matched" | "custom" | "unavailable" -- how to read `current`. */
  currentDetail: string;
  available: FailsafeDoctrine[];
}

/** One FS_* parameter write within a doctrine apply. */
export interface FailsafeCommandOutcome {
  name: string;
  code: CommandCode;
  message: string;
}

export interface FailsafeApplyResult {
  ok: boolean;
  doctrine: string;
  applied: FailsafeCommandOutcome[];
  /** The first write that failed, whole-apply aborting; null on success. */
  failed: FailsafeCommandOutcome | null;
  /** Advisory notes attached to this apply: airframe caveats plus a live
   * geofence-vs-doctrine conflict, if any. Never blocks the apply. */
  warnings: string[];
}

export type FleetFailsafeStatus =
  | 'applied'
  | 'skipped-armed'
  | 'no-link'
  | 'failed'
  | 'no-access';

export interface FleetFailsafeOutcome {
  droneId: DroneId;
  status: FleetFailsafeStatus;
  detail: string;
}

export interface FleetFailsafeResult {
  doctrine: string;
  results: FleetFailsafeOutcome[];
}
